import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  DocumentTextIcon,
  PhotoIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  PencilIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { InvoiceData, OCRFeedback } from '../types/invoice';
import InvoiceDataDisplay from './InvoiceDataDisplay';
import InvoiceEditForm from './InvoiceEditForm';
import FeedbackWidget from './FeedbackWidget';
import OCRApiService from '../services/api';
import toast from 'react-hot-toast';
import axios, { AxiosResponse } from 'axios';

interface FileComparisonViewProps {
  file: File;
  data: InvoiceData;
  filename?: string;
  invoiceId?: number; // DB id of the invoice — passed to save endpoint to prevent duplicates
  className?: string;
  previewUrl?: string;
  feedback?: OCRFeedback;
  onFeedbackSubmit: (feedback: OCRFeedback) => void | Promise<void>;
  onDataUpdate?: (updatedData: InvoiceData) => void;
  // Navigation props
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  fileSize?: number; // File size in bytes
  currentIndex?: number;
  totalCount?: number;
  version?: number; // Version number for version control
  compact?: boolean; // Hide tips, feedback, navigation controls (used in workspace)
  dirtyRef?: React.MutableRefObject<boolean>;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

const FileComparisonView: React.FC<FileComparisonViewProps> = ({
  file,
  data,
  filename,
  invoiceId,
  className = '',
  previewUrl,
  feedback,
  onFeedbackSubmit,
  onDataUpdate,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  fileSize,
  currentIndex,
  totalCount,
  version,
  compact = false,
  dirtyRef,
  saveRef,
}) => {
  const [showFilePreview, setShowFilePreview] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentData, setCurrentData] = useState<InvoiceData>(() => ({ ...data })); // Use lazy initialization
  const [isEditing, setIsEditing] = useState(true);
  const [documentHeight, setDocumentHeight] = useState<number>(0);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfZoom, setPdfZoom] = useState<number>(0); // percent; 0 means "fit width" (default)
  // Track when Ctrl is held so the wheel-zoom overlay can intercept ctrl+wheel
  // events before they reach the iframe's built-in PDF zoom (which would zoom
  // independently of our pdfZoom state, getting the two out of sync).
  const [ctrlHeld, setCtrlHeld] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.ctrlKey) setCtrlHeld(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 'Control' || !e.ctrlKey) setCtrlHeld(false);
    };
    const blur = () => setCtrlHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  const handleZoomWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -25 : 25;
    setPdfZoom((z) => {
      const base = z === 0 ? 100 : z;
      return Math.max(50, Math.min(300, base + delta));
    });
  };
  const pdfBlobUrlRef = useRef<string | null>(null);

  // currentData is initialized from data prop via useState above.
  // We do NOT sync it on every data prop change — that would reset the form
  // while the user is typing. Switching documents remounts via key={selectedFileId}.

  // Determine file type and content availability
  const isPDF = file?.type === 'application/pdf' || data?.file_url?.toLowerCase().endsWith('.pdf');
  const isImage = file?.type?.startsWith('image/') || false;
  // Check if file has actual content (not a placeholder from loaded invoices) or if file_url is available
  const hasFileContent = (file && file.size > 0) || !!data?.file_url;

  // Use provided previewUrl or create one as fallback
  const filePreviewUrl = useMemo(() => {
    if (previewUrl) {
      return previewUrl;
    }
    if (file && file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file, previewUrl]);

  // Create blob URL for PDF files or fetch from file_url with authorization
  React.useEffect(() => {
    let blobUrlToRevoke: string | null = null;

    // If file_url is available in invoice data, fetch it with authorization
    if (isPDF && data?.file_url) {
      const fetchPdfWithAuth = async () => {
        try {
          // Two auth paths: break-glass users carry a Bearer token in
          // localStorage, KeyNet SSO users carry the __Host-ks_session
          // cookie. Prefer whichever is present; always send credentials
          // so the cookie path works even on same-site cross-port.
          const token = localStorage.getItem('auth_token');

          const API_BASE_URL = process.env.REACT_APP_API_URL || '';

          // Construct full URL if file_url is relative
          const fullUrl = data.file_url!.startsWith('http')
            ? data.file_url!
            : `${API_BASE_URL}${data.file_url!.startsWith('/') ? '' : '/'}${data.file_url!}`;

          const response: AxiosResponse<Blob> = await axios.get(fullUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            withCredentials: true,
            responseType: 'blob',
          });

          // response.data is already a Blob when responseType is 'blob'
          const url = URL.createObjectURL(response.data);
          blobUrlToRevoke = url;
          pdfBlobUrlRef.current = url;
          setPdfLoaded(false);
          setPdfBlobUrl(url);
          setPdfLoadError(false);
        } catch (error) {
          console.error('Error fetching PDF from file_url:', error);
          setPdfLoadError(true);
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            toast.error('Session expired. Please log in again.');
          } else {
            toast.error('Could not load the document. Please try again.');
          }
        }
      };

      fetchPdfWithAuth();
      
      // Cleanup function - revoke blob URL when component unmounts or file_url changes
      return () => {
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
        }
      };
    }
    
    // Otherwise, create blob URL from file if it has content
    if (isPDF && hasFileContent && file && !data?.file_url) {
      try {
        const url = URL.createObjectURL(file as File);
        setPdfBlobUrl(url);
        setPdfLoadError(false);
        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error('Error creating blob URL for PDF:', error);
        setPdfLoadError(true);
      }
    } else if (!data?.file_url) {
      setPdfBlobUrl(null);
      setPdfLoadError(false);
    }
  }, [isPDF, hasFileContent, file, data?.file_url]);

  // Clean up image preview URL on unmount (PDF cleanup handled by the fetch effect above)
  React.useEffect(() => {
    return () => {
      if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  // Effect to match the height of the edit section to the document section
  React.useEffect(() => {
    if (!isEditing) { // Only update height when not editing to prevent interference
      const updateHeight = () => {
        const documentSection = document.getElementById('document-preview-section');
        if (documentSection && showFilePreview && !isExpanded) {
          const height = documentSection.offsetHeight;
          setDocumentHeight(height);
        }
      };

      // Update height after render
      const timer = setTimeout(updateHeight, 100);

      // Update on window resize
      window.addEventListener('resize', updateHeight);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, [showFilePreview, isExpanded, filePreviewUrl, isEditing]);

  // Plain render function (not a component) — invoking it returns JSX
  // directly so React reconciles the inner element tree by its stable types
  // (div, iframe, etc.) instead of by a parent component identity that would
  // churn on every render. See note above editFormJsx for the full story.
  const renderFilePreview = (): React.ReactNode => {
    if (!showFilePreview) {
      return null;
    }

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden ${
        isExpanded ? 'col-span-2' : ''
      }`} id="document-preview-section">
        <div className="bg-brand-600 dark:bg-brand-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isImage ? (
                <PhotoIcon className="h-6 w-6 text-white mr-3" />
              ) : (
                <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
              )}
              <div>
                <h3 className="text-responsive-xl font-semibold text-white">
                  Original Document
                </h3>
                <div>
                  <p className="text-purple-100 text-sm">
                    {filename || file.name}
                  </p>
                  <p className="text-purple-200 text-xs mt-0.5">
                    {fileSize 
                      ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
                      : file.size 
                        ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                        : 'Unknown size'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilePreview(false)}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-white/90 hover:bg-white/20 rounded-lg transition-colors duration-200 text-sm"
                title="Hide invoice image"
              >
                <EyeSlashIcon className="h-4 w-4" />
                <span>Hide</span>
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors duration-200"
                title={isExpanded ? 'Show side-by-side' : 'Expand preview'}
              >
                {isExpanded ? (
                  <ArrowsPointingInIcon className="h-5 w-5" />
                ) : (
                  <ArrowsPointingOutIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isImage && filePreviewUrl ? (
            <div className="relative">
              <img
                src={filePreviewUrl}
                alt="Uploaded document"
                className="w-full h-auto object-contain rounded-lg shadow-md border border-gray-200 dark:border-gray-600"
                style={{ maxWidth: '100%' }}
              />
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  📄 Original uploaded image - compare with extracted data on the right
                </p>
              </div>
            </div>
          ) : isPDF ? (
            <div className="relative h-full flex flex-col">
              {/* Zoom toolbar — sits above the PDF so it doesn't cover content */}
              {pdfBlobUrl && !pdfLoadError && (
                <div className="flex items-center justify-end gap-1 mb-2 px-1">
                  <div className="flex items-center gap-0.5 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-0.5">
                    <button
                      type="button"
                      onClick={() => setPdfZoom(z => Math.max(50, (z === 0 ? 100 : z) - 25))}
                      className="w-7 h-7 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
                      title="Zoom out"
                    >
                      −
                    </button>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 px-1 min-w-[2.75rem] text-center select-none">
                      {pdfZoom === 0 ? 'Fit' : `${pdfZoom}%`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPdfZoom(z => Math.min(300, (z === 0 ? 100 : z) + 25))}
                      className="w-7 h-7 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 rounded"
                      title="Zoom in"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => setPdfZoom(0)}
                      className="w-7 h-7 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded"
                      title="Reset to fit width"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex-1 bg-white rounded-lg shadow-md border border-gray-200 dark:border-gray-600 overflow-hidden">
                {pdfBlobUrl && !pdfLoadError ? (
                  <div className="relative" style={{ minHeight: '600px' }}>
                    {/* Loading placeholder — visible until iframe finishes loading */}
                    {!pdfLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-700">
                        <div className="text-center animate-pulse">
                          <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-500 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">Loading document...</p>
                        </div>
                      </div>
                    )}
                    {/* Zoom works by changing the iframe's CSS width — the
                        PDF viewer's `view=FitH` fits the page width to the
                        iframe, so a wider iframe renders a larger PDF.
                        URL fragment never changes once loaded → no reload.
                        Outer div has overflow:auto so the scaled iframe
                        gets a horizontal scrollbar at zoom > 100%.
                        The ctrl-held overlay intercepts ctrl+wheel events
                        before they reach the iframe's built-in zoom, routing
                        them through our pdfZoom state instead. Without it,
                        ctrl+wheel would zoom the PDF viewer internally while
                        our state stayed put, and the next button click would
                        snap back to the state-tracked zoom level. */}
                    <div
                      style={{ overflow: 'auto', width: '100%', height: '100%', minHeight: '600px', position: 'relative' }}
                      onWheel={handleZoomWheel}
                    >
                      <iframe
                        src={pdfBlobUrl + '#toolbar=0&navpanes=0&view=FitH'}
                        className={`border-0 bg-white transition-opacity duration-200 ${pdfLoaded ? 'opacity-100' : 'opacity-0'}`}
                        style={{
                          width: pdfZoom === 0 ? '100%' : `${pdfZoom}%`,
                          minHeight: '600px',
                          height: pdfZoom === 0 ? '100%' : `${Math.max(600, pdfZoom * 6)}px`,
                        }}
                        title="PDF Preview"
                        onLoad={() => {
                          setPdfLoaded(true);
                          setPdfLoadError(false);
                        }}
                        onError={() => {
                          console.error('PDF iframe load error');
                          setPdfLoadError(true);
                          toast.error('Could not load the document.');
                        }}
                      />
                      {ctrlHeld && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            cursor: 'zoom-in',
                          }}
                          onWheel={handleZoomWheel}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96 bg-gray-50 dark:bg-gray-700">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
                      {pdfLoadError
                        ? 'Failed to load PDF document. The file may be corrupted or unavailable.'
                        : data?.file_url
                          ? 'Loading PDF from server...'
                          : hasFileContent 
                            ? 'PDF preview not available for this file'
                            : 'PDF preview not available. This invoice was loaded from the database and the original file is not available.'}
                    </p>
                    {hasFileContent && (
                      <button
                        onClick={() => {
                          try {
                            if (file && typeof file === 'object' && 'size' in file && file.size > 0) {
                              const url = URL.createObjectURL(file as File);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 1000);
                            } else {
                              toast.error('Document file is not available.');
                            }
                          } catch (error) {
                            console.error('Error opening PDF:', error);
                            toast.error('Could not open document.');
                          }
                        }}
                        className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                      >
                        Open in New Tab
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 text-center flex-shrink-0">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  📄 PDF document preview - compare with extracted data on the right
                </p>
                <div className="mt-2 flex justify-center space-x-2">
                  {hasFileContent && file && typeof file === 'object' && 'size' in file && (
                    <button
                      onClick={() => {
                        try {
                          if (file.size > 0) {
                            const url = URL.createObjectURL(file as File);
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } else {
                            toast.error('File content is not available');
                          }
                        } catch (error) {
                          console.error('Error opening PDF:', error);
                          toast.error('Unable to open PDF file');
                        }
                      }}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      Open in Full Screen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[500px] py-12 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <DocumentTextIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
              <h4 className="text-responsive-lg font-semibold text-gray-900 dark:text-white mb-2">
                File Preview Not Available
              </h4>
              <p className="text-gray-600 dark:text-gray-400 text-center">
                Preview is not available for this file type, but the document has been processed successfully.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSave = async (updatedData: InvoiceData) => {
    try {
      toast.loading('Saving changes...', { id: 'save-invoice' });
      
      // Save to backend
      await OCRApiService.saveEditedInvoiceData(filename || file.name, updatedData, invoiceId);

      // Update local state ONLY after successful save
      // Preserve file_url from the previous data — the form doesn't track it
      // and losing it makes the PDF preview disappear until next reload
      setCurrentData(prev => ({ ...updatedData, file_url: updatedData.file_url || prev.file_url }));
      
      // Exit edit mode
      setIsEditing(false);
      
      // Notify parent component
      if (onDataUpdate) {
        onDataUpdate(updatedData);
      }
      
      toast.success('Invoice saved.', { id: 'save-invoice' });
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Could not save. Please try again.', { id: 'save-invoice' });
      throw error; // Re-throw to let the component handle it
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // NOTE: these section JSX blocks used to be defined as nested function
  // components (`const EditFormSection = () => ...`). Doing that creates a
  // brand-new component type on every parent render, which makes React
  // unmount + remount everything inside on each state change — InvoiceEditForm
  // -> VendorLookup -> mount-time auto-lookup fires every zoom click, etc.
  // Inlining the JSX directly fixes that. If they grow large again, extract
  // them as TOP-LEVEL components (outside FileComparisonView) so the type
  // identity stays stable across renders.
  const extractedDataJsx = (
    <div
      className={`${isExpanded && showFilePreview ? 'hidden' : ''}`}
      style={{ height: documentHeight > 0 ? `${documentHeight}px` : 'auto' }}
    >
      <InvoiceDataDisplay
        data={currentData}
        filename={filename}
        className="h-full"
      />
    </div>
  );

  const editFormJsx = (
    <div
      className={`${isExpanded && showFilePreview ? 'hidden' : ''} relative`}
      style={{ height: documentHeight > 0 ? `${documentHeight}px` : 'auto' }}
    >
      <InvoiceEditForm
        key={`edit-form-stable`}
        data={currentData}
        filename={filename}
        onSave={handleSave}
        onCancel={handleCancelEdit}
        dirtyRef={dirtyRef}
        saveRef={saveRef}
        className="h-full relative z-20"
      />
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Show Invoice Image button — only when preview is hidden */}
      {!showFilePreview && (
        <button
          onClick={() => setShowFilePreview(true)}
          className="flex items-center space-x-2 px-4 py-2.5 rounded-lg font-semibold transition-colors bg-brand-600 text-white hover:bg-brand-700"
        >
          <EyeIcon className="h-5 w-5" />
          <span className="text-sm">Show Invoice Image</span>
        </button>
      )}

      {/* Main Content */}
      {showFilePreview && !isExpanded ? (
        /* Side-by-side layout - responsive: stacked on mobile, side-by-side on tablet+ */
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 items-start">
          <div className="order-1 xl:order-1 xl:sticky xl:top-4 xl:self-start">
            {renderFilePreview()}
          </div>
          <div className="order-2 xl:order-2">
            {editFormJsx}
          </div>
        </div>
      ) : showFilePreview && isExpanded ? (
        /* Expanded file preview */
        <div className="grid grid-cols-1">
          {renderFilePreview()}
        </div>
      ) : (
        /* Only extracted data or edit form */
        <div className="grid grid-cols-1">
          {isEditing ? editFormJsx : extractedDataJsx}
        </div>
      )}

      {/* Helpful tips — hidden in compact mode */}
      {!compact && (
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
            <PencilIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white">Editing Tips</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center space-x-3">
            <span className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></span>
            <span className="text-gray-700 dark:text-gray-300">
              Edit any extracted field directly in the form
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="w-2 h-2 bg-brand-400 rounded-full flex-shrink-0"></span>
            <span className="text-gray-700 dark:text-gray-300">
              Click "View Receipt" to see the original document
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0"></span>
            <span className="text-gray-700 dark:text-gray-300">
              Use "View Data" to see the formatted invoice
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0"></span>
            <span className="text-gray-700 dark:text-gray-300">
              Changes are saved when you click "Save"
            </span>
          </div>
        </div>
      </div>
      )}

      {/* Feedback Widget — hidden in compact mode */}
      {!compact && (
      <div className="mt-8">
        <FeedbackWidget
          filename={filename || file.name}
          onFeedbackSubmit={onFeedbackSubmit}
          initialFeedback={feedback}
        />
      </div>
      )}

      {/* Navigation Controls — hidden in compact mode (sidebar handles navigation) */}
      {!compact && (onPrevious || onNext || (isEditing && onDataUpdate)) && (
        <div className="mt-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between">
              {/* Navigation Info */}
              <div className="flex items-center space-x-4">
                {totalCount && currentIndex !== undefined && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <DocumentTextIcon className="h-4 w-4" />
                    <span>Invoice {currentIndex + 1} of {totalCount}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {/* Outer Save button removed — use the Save Changes button inside the edit form instead */}

                {/* Previous Button */}
                <button
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    hasPrevious
                      ? 'bg-gradient-to-r from-brand-500 to-purple-600 text-white hover:from-brand-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span>Previous</span>
                </button>

                {/* Next Button */}
                <button
                  onClick={onNext}
                  disabled={!hasNext}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    hasNext
                      ? 'bg-gradient-to-r from-brand-500 to-purple-600 text-white hover:from-brand-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span>Next</span>
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(FileComparisonView);
